import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Check, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { pricingData } from '../data/mock';

const Pricing = ({ onContactClick }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % pricingData.plans.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + pricingData.plans.length) % pricingData.plans.length);
  };

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              {pricingData.title}
            </h2>
            <div className="w-24 h-1 bg-[#2d8b8b] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-2">
              {pricingData.subtitle}
            </p>
            <p className="text-sm text-gray-500">{pricingData.note}</p>
          </div>

          {/* Desktop Grid */}
          {!isMobile && (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {pricingData.plans.map((plan, index) => (
                <Card
                  key={index}
                  className={`relative p-6 flex flex-col h-full ${
                    plan.recommended
                      ? 'border-2 border-[#2d8b8b] shadow-xl'
                      : 'border border-gray-200'
                  }`}
                >
                  {plan.recommended && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-[#2d8b8b] text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                        <Star className="w-4 h-4" fill="currentColor" />
                        Recommended
                      </div>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-[#1e3a5f] mb-2">{plan.name}</h3>
                    <div className="mb-3">
                      <span className="text-4xl font-bold text-[#2d8b8b]">{plan.price}</span>
                      <span className="text-gray-600 text-sm block mt-1">{plan.period}</span>
                    </div>
                    <p className="text-sm text-gray-600">{plan.description}</p>
                  </div>

                  <div className="flex-1 mb-6 max-h-[280px] overflow-y-auto scrollbar-thin">
                    <ul className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-[#2d8b8b] flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-4">{plan.note}</p>
                    <Button
                      onClick={onContactClick}
                      className={`w-full ${
                        plan.recommended
                          ? 'bg-[#2d8b8b] hover:bg-[#236b6b] text-white'
                          : 'bg-[#1e3a5f] hover:bg-[#2d5580] text-white'
                      }`}
                    >
                      {plan.cta}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Mobile Slider */}
          {isMobile && (
            <div className="relative">
              <Card
                className={`p-6 ${
                  pricingData.plans[currentSlide].recommended
                    ? 'border-2 border-[#2d8b8b] shadow-xl'
                    : 'border border-gray-200'
                }`}
              >
                {pricingData.plans[currentSlide].recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-[#2d8b8b] text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                      <Star className="w-4 h-4" fill="currentColor" />
                      Recommended
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-[#1e3a5f] mb-2">
                    {pricingData.plans[currentSlide].name}
                  </h3>
                  <div className="mb-3">
                    <span className="text-4xl font-bold text-[#2d8b8b]">
                      {pricingData.plans[currentSlide].price}
                    </span>
                    <span className="text-gray-600 text-sm block mt-1">
                      {pricingData.plans[currentSlide].period}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {pricingData.plans[currentSlide].description}
                  </p>
                </div>

                <div className="mb-6 max-h-[300px] overflow-y-auto">
                  <ul className="space-y-3">
                    {pricingData.plans[currentSlide].features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-[#2d8b8b] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-4">
                    {pricingData.plans[currentSlide].note}
                  </p>
                  <Button
                    onClick={onContactClick}
                    className="w-full bg-[#2d8b8b] hover:bg-[#236b6b] text-white"
                  >
                    {pricingData.plans[currentSlide].cta}
                  </Button>
                </div>
              </Card>

              {/* Navigation Controls */}
              <div className="flex items-center justify-center gap-4 mt-6">
                <button
                  onClick={prevSlide}
                  className="w-10 h-10 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center hover:bg-[#2d5580] transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="flex gap-2">
                  {pricingData.plans.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        currentSlide === index
                          ? 'bg-[#2d8b8b] w-8'
                          : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextSlide}
                  className="w-10 h-10 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center hover:bg-[#2d5580] transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
